import React from 'react';
import moment from 'moment';
import { EMPLOYEE_CONTACT_TYPES } from 'constants/companies';

const display = (value) =>
  value === null || value === undefined || value === '' ? '—' : value;

const displayBool = (value, t) =>
  value === true || value === false
    ? value
      ? t('Yes')
      : t('No')
    : display(value);

const displayDate = (value) =>
  value && moment(value).isValid() ? moment(value).format('DD MMMM YYYY') : '—';

const fileLink = (file) =>
  file?.url ? (
    <a href={file.url} target='_blank' rel='noreferrer'>
      {file.filename || file.file_name || file.url}
    </a>
  ) : (
    '—'
  );

const PAYMENT_FIELDS = [
  { key: 'payment_mastercard', label: 'Mastercard' },
  { key: 'payment_visa', label: 'Visa' },
  { key: 'payment_american_express', label: 'American Express' },
  { key: 'payment_alipay', label: 'Ali Pay' },
  { key: 'payment_union_pay', label: 'Union Pay' },
  { key: 'payment_wechat_pay', label: 'Wechat Pay' },
  { key: 'payment_paypal', label: 'PayPal' },
  { key: 'payment_apple_pay', label: 'Apple Pay' },
  { key: 'payment_poste_pay', label: 'PostePay' },
  { key: 'payment_satispay', label: 'SatisPay' },
  { key: 'payment_klarna', label: 'Klarna' },
  { key: 'payment_scalapay', label: 'Scalapay' },
];

const TABLE_PRICE_ROWS = [
  { key: 'to_30', label: 'Fino a € 30,00' },
  { key: 'from_30_to_50', label: 'Da € 30,01 € 50,00' },
  { key: 'from_50_to_100', label: 'Da € 50,01 a € 100,00' },
  { key: 'from_100_to_150', label: 'Da € 100.01 a € 150,00' },
  { key: 'from_150_to_200', label: 'Da € 150,01 a € 200,00' },
  { key: 'from_200_to_250', label: 'Da € 200,01 a € 250,00' },
  { key: 'from_250', label: 'Oltre € 250,01' },
];

const getTablePriceFields = (priceRow, t, prefix) => {
  if (!priceRow) {
    return [];
  }
  return TABLE_PRICE_ROWS.map(({ key, label }) => ({
    text: `${prefix} - ${label}`,
    value: display(priceRow[key]),
  }));
};

const getDeliveryTypeLabel = (type, t) => {
  const map = {
    free: t('Spedizione sempre gratuita'),
    box: t('Costi spedizione a box'),
    table: t('Costi spedizione per soglia di acquisto'),
  };
  return display(map[type] || type);
};

const getReturnTypeLabel = (type, t) => {
  const map = {
    free: t('Spedizione reso sempre gratuito'),
    box: t('Costi spedizione reso a box'),
    table: t('Costi spedizione reso per soglia di acquisto'),
  };
  return display(map[type] || type);
};

const getInvoiceYearTypeLabel = (type, t) => {
  const map = {
    half_year: t('Fatturato semestrale'),
    year: t('Fatturato annuale'),
  };
  return display(map[type] || type);
};

const findContact = (item, type, index) => {
  const contacts = item?.company_employee_contacts || [];
  return contacts.find((c) => c.contact_type === type.value) || contacts[index];
};

export const getCompanyViewSections = (t, item, { isRoot } = {}) => {
  if (!item?.id) {
    return [];
  }

  const cd = item?.company_data || {};
  const cc = item?.company_click_collect || {};
  const prices = cc?.company_table_prices || [];
  const deliveryPrices =
    prices.find((p) => p.price_type === 'delivery') || prices[0];
  const returnPrices =
    prices.find((p) => p.price_type === 'return') || prices[1];

  const enabledPayments = PAYMENT_FIELDS.filter(({ key }) => cd[key]).map(
    ({ label }) => label,
  );

  const sections = [
    {
      title: t('Info Generali'),
      fields: [
        { text: t('Nome Insegna'), value: display(item.title) },
        {
          text: t('Outlet Village'),
          value: display(item.shopping_center?.title),
        },
        { text: t('Nego ID'), value: display(item.nego_id) },
        { text: t('IVA (solo numero)'), value: display(item.vat) },
        { text: t('Catena'), value: display(item.chain?.title) },
        {
          text: t('Categoria insegna'),
          value: display(item.company_category?.title),
        },
        { text: 'Data di apertura', value: displayDate(item.opening_date) },
        { text: 'Data di chiusura', value: displayDate(item.closing_date) },
        {
          text: 'Data inizio contratto',
          value: displayDate(item.contract_start_date),
        },
        {
          text: 'Data di fine del contratto',
          value: displayDate(item.contract_end_date),
        },
        { text: 'Mq Unità', value: display(item.place_area) },
        { text: t('Codice Unità'), value: display(item.location_code) },
        {
          text: 'Categoria merceologica',
          value: display(item.commodity_category),
        },
        {
          text: 'Sottocategoria merceologica',
          value: display(item.company_child_category?.title),
        },
        {
          text: 'Segmento',
          value: display(item.prestige_level?.title),
        },
        { text: 'Food', value: displayBool(item.food, t) },
      ],
    },
    {
      title: t('Fatturati'),
      fields: [
        {
          text: t('Tipo fatturato'),
          value: getInvoiceYearTypeLabel(item.invoice_year_type, t),
        },
        {
          text: t('Escludi notifica reminder'),
          value: displayBool(item.exclude_reminder_notification, t),
        },
      ],
    },
    {
      title: t('Contatti Store'),
      fields: [
        { text: 'Phone', value: display(cd.phone) },
        { text: 'E-mail', value: display(cd.email) },
        { text: 'Whatsapp', value: display(cd.whatsapp) },
        { text: 'Whatsapp 2', value: display(cd.whatsapp2) },
        { text: 'Whatsapp 3', value: display(cd.whatsapp3) },
      ],
    },
    {
      title: t('Ragione Sociale Store'),
      fields: [
        { text: t('Denominazione'), value: display(cd.address_denominazione) },
        { text: t('Indirizzo'), value: display(cd.address_address) },
        { text: t('Citta'), value: display(cd.address_city) },
        { text: t('Cap'), value: display(cd.address_postal_code) },
        { text: t('Provincia'), value: display(cd.address_province) },
        {
          text: t('P.IVA / Codice Fiscale'),
          value: display(cd.address_tax_code),
        },
        { text: t('Codice Univoco'), value: display(cd.address_unique_code) },
        { text: t('Pec aziendale'), value: display(cd.pec_aziendale) },
      ],
    },
    {
      title: t('Contatti Aziendali'),
      fields: EMPLOYEE_CONTACT_TYPES.flatMap((type, index) => {
        const contact = findContact(item, type, index);
        return [
          { text: `${type.title} - Nome`, value: display(contact?.name) },
          { text: `${type.title} - Cognome`, value: display(contact?.surname) },
          { text: `${type.title} - Telefono`, value: display(contact?.phone) },
          { text: `${type.title} - Email`, value: display(contact?.email) },
        ];
      }),
    },
    {
      title: t('Personale'),
      fields: [
        {
          text: t('Nr addetti in store (Full time)'),
          value: display(cd.number_full_time_employees),
        },
        {
          text: t('Nr addetti in store (Part-time)'),
          value: display(cd.number_part_time_employees),
        },
      ],
    },
    {
      title: t('Convenzioni Village'),
      fields: [
        {
          text: t('Sconto dipendenti village'),
          value: displayBool(cd.village_contract, t),
        },
        { text: t('One day card'), value: displayBool(cd.one_day_card, t) },
        { text: t('Gift Card'), value: displayBool(cd.gift_cards, t) },
      ],
    },
    {
      title: t('Pagamenti accettati'),
      fields: [
        {
          text: t('Metodi di pagamento'),
          value: enabledPayments.length ? enabledPayments.join(', ') : '—',
        },
      ],
    },
    {
      title: t('Smart shopping'),
      fields: [
        {
          text: t('Click & Reserve'),
          value: displayBool(cc.is_click_reserve, t),
        },
        {
          text: t('Click & Collect (Details are required)'),
          value: displayBool(cc.is_click_collect, t),
        },
        {
          text: t('Shop online (Details are required)'),
          value: displayBool(cc.is_online_shop, t),
        },
      ],
    },
    {
      title: t('Bank'),
      fields: [
        { text: t('Api key'), value: display(cc.api_key) },
        { text: t('Shop login'), value: display(cc.shop_login) },
        { text: t('Dev mode'), value: displayBool(cc.dev_mode, t) },
        {
          text: t('Attiva accesso backend'),
          value: displayBool(cc.backend_access_click_collect, t),
        },
        {
          text: t('Attiva insegna online'),
          value: displayBool(cc.view_online_click_collect, t),
        },
        {
          text: t('Vuoi condividere numero Whatsapp?'),
          value: displayBool(cc.is_whatsapp, t),
        },
        {
          text: t('Data inizio contratto'),
          value: displayDate(cc.date_begin),
        },
        {
          text: t('Data termine contratto'),
          value: displayDate(cc.date_end),
        },
        {
          text: t('Macro Categoria Smart Shopping'),
          value: display(cc.macro_category),
        },
        {
          text: t('Modello scheda prodotto'),
          value: display(cc.product_sheet_model),
        },
        {
          text: t('Limite articoli pubblicabili'),
          value: display(cc.products_limit),
        },
        { text: t('Email support'), value: display(cc.email_support) },
      ],
    },
    {
      title: t('Condizioni resi, Cambi, Garanzia'),
      fields: [
        { text: 'Resi', value: display(cc.return_product) },
        { text: 'Cambi', value: display(cc.change_product) },
        { text: 'Garanzia', value: display(cc.guarantee_product) },
        {
          text: t('Shop Policy Smart Shopping'),
          value: fileLink(cc.shop_policy_click_collect),
        },
      ],
    },
    {
      title: t('Tariffe spedizioni acquisti'),
      fields: [
        {
          text: t('Tipo spedizione'),
          value: getDeliveryTypeLabel(cc.delivery_type, t),
        },
        {
          text: t('Spese spedizione fisse a box'),
          value: display(cc.delivery_box_price),
        },
        ...getTablePriceFields(
          cc.delivery_type === 'table' ? deliveryPrices : null,
          t,
          t('Acquisti'),
        ),
      ],
    },
    {
      title: t('TARIFFE SPEDIZIONE RESI'),
      fields: [
        {
          text: t('Tipo spedizione reso'),
          value: getReturnTypeLabel(cc.return_type, t),
        },
        {
          text: t('Costi spedizione a box'),
          value: display(cc.return_box_price),
        },
        ...getTablePriceFields(
          cc.return_type === 'table' ? returnPrices : null,
          t,
          t('Resi'),
        ),
      ],
    },
    {
      title: t('Insegna Smart Shopping'),
      fields: [
        {
          text: t('Immagine header del brand'),
          value: fileLink(cc.header_image_click_collect),
        },
        { text: t('Logo'), value: fileLink(cc.logo_click_collect) },
        {
          text: t('Insegna Brand Smart Shopping'),
          value: fileLink(cc.company_image_click_collect),
        },
      ],
    },
  ];

  if (isRoot) {
    sections.splice(2, 0, {
      title: 'Competitors',
      fields: [
        {
          text: 'Competitor 1',
          value: display(
            item.company_competitors?.[0]?.competior?.title ||
              item.company_competitors?.[0]?.competitor?.title,
          ),
        },
        {
          text: 'Competitor 2',
          value: display(
            item.company_competitors?.[1]?.competior?.title ||
              item.company_competitors?.[1]?.competitor?.title,
          ),
        },
      ],
    });
  }

  return sections;
};
